/**
 * Music and stings for the shell, and the one place that decides *when* the
 * browser is allowed to make a sound.
 *
 * A page cannot start audio until it has had a real user gesture: an
 * `AudioContext` created before one sits in `suspended` and every `play()` on
 * a media element is rejected. So this module refuses to play anything until
 * `unlock()` has run — and `unlock()` creates the context inside the gesture
 * and awaits `resume()` before flipping the flag. Nothing downstream has to
 * remember the rule; `playMusic`/`playSfx` simply do nothing before it holds,
 * which is what makes "no `play()` before `resume()`" an invariant rather than
 * a convention.
 *
 * Once unlocked, each element is routed into the context through a per-bus
 * `GainNode`, so the volume comes from the same persisted sliders `/fly`'s
 * settings popover writes (`audio/settings.ts`, read-only here) and a
 * suspended context silences everything at once — which is how a hidden tab
 * pauses the intro's sound in lockstep with its clock.
 */

import { browserSettingsStore, effectiveBusVolume, loadAudioSettings } from "../../audio/settings.ts";

/** What plays under any screen that does not name its own theme. */
export const SHELL_THEME = "TITLE";

export const MUSIC_PATHS: Readonly<Record<string, string>> = {
  DYNAMIX: "/baked/music/DYNAMIX.ogg",
  TITLE: "/baked/music/TITLE.ogg",
  /** The three career-cutscene themes this port wires a scene to — see `shell/cutscene/career-cutscenes.ts`. */
  WAREND1: "/baked/music/WAREND1.ogg",
  MEDAL: "/baked/music/MEDAL.ogg",
  PROMOTE: "/baked/music/PROMOTE.ogg",
  /**
   * The Board of Inquiry's own theme. `PS.EXE` names `board.sng` beside
   * `boi.dat`, the file the hearing's text pool comes from, and no `.TTM` in the
   * corpus is a Board of Inquiry scene — so this one plays *under a screen*
   * rather than under a cutscene (`career/career-cutscenes.ts`' header,
   * `docs/formats/sound-music.md`'s cutscene-music table).
   */
  BOARD: "/baked/music/BOARD.ogg",
};

/**
 * `PLAY SFX n` indexes the currently selected sound bank. `SIMSNDS.SNG` bakes
 * to one file per cue (`sfx/SIMSNDS-nn.ogg`); `SHLSNDS2.SNG` holds a single
 * cue and bakes to one unsuffixed file, so slot 1's only valid index is 0.
 */
export function sfxPath(slot: number, id: number): string {
  if (slot === 1) return "/baked/sfx/SHLSNDS2.ogg";
  return `/baked/sfx/SIMSNDS-${String(id).padStart(2, "0")}.ogg`;
}

/** A file that never reports itself ready must not hold the start gate shut forever. */
export const PRELOAD_TIMEOUT_MS = 15_000;
const HAVE_ENOUGH_DATA = 4;

interface Buses {
  readonly music: number;
  readonly sfx: number;
}

function readBuses(): Buses {
  const store = browserSettingsStore();
  if (!store) return { music: 0.7, sfx: 0.9 };
  const settings = loadAudioSettings(store);
  return { music: effectiveBusVolume(settings, settings.music), sfx: effectiveBusVolume(settings, settings.sfx) };
}

/** Same feature-detect the flight engine and the asset viewers use — `bun test` has no DOM at all. */
function hasAudioContext(): boolean {
  return typeof window !== "undefined" && typeof window.AudioContext === "function";
}

interface AudioGraph {
  readonly context: AudioContext;
  readonly music: GainNode;
  readonly sfx: GainNode;
}

function createAudioGraph(): AudioGraph | undefined {
  if (!hasAudioContext()) return undefined;
  const context = new AudioContext();
  const music = context.createGain();
  music.connect(context.destination);
  const sfx = context.createGain();
  sfx.connect(context.destination);
  return { context, music, sfx };
}

type BusName = "music" | "sfx";

interface AudioState {
  buses: Buses;
  graph: AudioGraph | undefined;
  unlocked: boolean;
  music: HTMLAudioElement | undefined;
  musicName: string | undefined;
  readonly elements: Map<string, HTMLAudioElement>;
  readonly routed: Set<HTMLAudioElement>;
  readonly sounding: Set<HTMLAudioElement>;
}

function elementFor(state: AudioState, path: string): HTMLAudioElement | undefined {
  const cached = state.elements.get(path);
  if (cached) return cached;
  if (typeof Audio !== "function") return undefined;
  const element = new Audio();
  element.preload = "auto";
  element.src = path;
  state.elements.set(path, element);
  return element;
}

function whenLoaded(element: HTMLAudioElement): Promise<void> {
  if (element.readyState >= HAVE_ENOUGH_DATA) return Promise.resolve();
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const settle = (): void => {
      if (timer !== undefined) clearTimeout(timer);
      element.removeEventListener("canplaythrough", settle);
      element.removeEventListener("error", settle);
      resolve();
    };
    element.addEventListener("canplaythrough", settle, { once: true });
    element.addEventListener("error", settle, { once: true });
    timer = setTimeout(settle, PRELOAD_TIMEOUT_MS);
    element.load();
  });
}

/**
 * A routed element's own `volume` is left alone and the bus gain carries the
 * setting; an unroutable one (no context, or a browser that refuses a second
 * source for the same element) falls back to the element's volume so it is
 * quiet-but-correct rather than silent.
 */
function route(state: AudioState, element: HTMLAudioElement, bus: BusName): void {
  if (state.routed.has(element)) return;
  const graph = state.graph;
  if (!graph) {
    element.volume = state.buses[bus];
    return;
  }
  try {
    graph.context.createMediaElementSource(element).connect(graph[bus]);
    state.routed.add(element);
  } catch {
    element.volume = state.buses[bus];
  }
}

function start(element: HTMLAudioElement): void {
  void element.play().catch(() => undefined);
}

async function unlock(state: AudioState): Promise<void> {
  if (state.unlocked) return;
  state.buses = readBuses();
  state.graph ??= createAudioGraph();
  const graph = state.graph;
  if (graph) {
    graph.music.gain.setValueAtTime(state.buses.music, graph.context.currentTime);
    graph.sfx.gain.setValueAtTime(state.buses.sfx, graph.context.currentTime);
    try {
      await graph.context.resume();
    } catch {
      // A context that refuses to resume leaves the game silent, never stuck: the intro still runs.
    }
  }
  state.unlocked = true;
}

function stopMusic(state: AudioState): void {
  state.music?.pause();
  state.music = undefined;
  state.musicName = undefined;
}

function playMusic(state: AudioState, name: string, loop: boolean): void {
  const path = MUSIC_PATHS[name];
  if (!path || !state.unlocked || state.musicName === name) return;
  stopMusic(state);
  const element = elementFor(state, path);
  if (!element) return;
  route(state, element, "music");
  element.loop = loop;
  element.currentTime = 0;
  state.music = element;
  state.musicName = name;
  start(element);
}

function playSfx(state: AudioState, slot: number, id: number): void {
  if (!state.unlocked) return;
  const element = elementFor(state, sfxPath(slot, id));
  if (!element) return;
  route(state, element, "sfx");
  element.currentTime = 0;
  state.sounding.add(element);
  start(element);
}

export interface ShellAudio {
  /** Fetches and buffers every file the caller will later play. Settles even when a file is missing. */
  preload(paths: readonly string[]): Promise<void>;
  /** The activation gesture. Creates the context and awaits `resume()`; only then may anything play. */
  unlock(): Promise<void>;
  unlocked(): boolean;
  playMusic(name: string, loop: boolean): void;
  stopMusic(): void;
  currentMusic(): string | undefined;
  playSfx(slot: number, id: number): void;
  stopSfx(slot: number, id: number): void;
  /** Everything still sounding from the sound banks — what a skip has to silence before the menu. */
  stopAllSfx(): void;
  /** Tab hidden: freeze the context and hold every element where it is. */
  suspend(): void;
  resume(): void;
  dispose(): void;
}

export function createShellAudio(): ShellAudio {
  const state: AudioState = {
    buses: readBuses(),
    graph: undefined,
    unlocked: false,
    music: undefined,
    musicName: undefined,
    elements: new Map(),
    routed: new Set(),
    sounding: new Set(),
  };

  return {
    async preload(paths) {
      await Promise.all(
        paths.map(async (path) => {
          const element = elementFor(state, path);
          if (element) await whenLoaded(element);
        }),
      );
    },
    unlock: () => unlock(state),
    unlocked: () => state.unlocked,
    playMusic: (name, loop) => playMusic(state, name, loop),
    stopMusic: () => stopMusic(state),
    currentMusic: () => state.musicName,
    playSfx: (slot, id) => playSfx(state, slot, id),
    stopSfx(slot, id) {
      const element = state.elements.get(sfxPath(slot, id));
      if (!element) return;
      element.pause();
      state.sounding.delete(element);
    },
    stopAllSfx() {
      for (const element of state.sounding) element.pause();
      state.sounding.clear();
    },
    suspend() {
      state.music?.pause();
      for (const element of state.sounding) element.pause();
      void state.graph?.context.suspend().catch(() => undefined);
    },
    resume() {
      void state.graph?.context.resume().catch(() => undefined);
      if (state.music) start(state.music);
      for (const element of state.sounding) start(element);
    },
    dispose() {
      stopMusic(state);
      for (const element of state.elements.values()) element.pause();
      state.elements.clear();
      state.routed.clear();
      state.sounding.clear();
      state.unlocked = false;
      void state.graph?.context.close().catch(() => undefined);
      state.graph = undefined;
    },
  };
}
